import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

/**
 * Higher Order Component to provide React Router v6 hooks to class components
 * Compatible with React Router v5's withRouter API
 * 
 * Usage:
 * export default WithRouter(MyComponent);
 * 
 * In your class component, access router functionality via props (same as old withRouter):
 * - this.props.history - history object with methods (push, replace, go, goBack, goForward)
 * - this.props.location - location object (same as old withRouter)
 * - this.props.match - match object with params, path, url, isExact (same as old withRouter)
 * 
 * New React Router v6 API also available:
 * - this.props.navigate - navigation function (alternative to history.push)
 * - this.props.params - route parameters (alternative to match.params)
 */
function WithRouter(Component) {
    function WithRouterWrapper(props) {
        const navigate = useNavigate();
        const location = useLocation();
        const params = useParams();
        
        // Create history object compatible with React Router v5 API
        const history = React.useMemo(() => ({
            push: (to, state) => {
                if (typeof to === 'string') {
                    navigate(to, { state });
                } else if (to && typeof to === 'object') {
                    // Handle { pathname, search, state } format
                    const path = to.pathname + (to.search || '');
                    navigate(path, { state: to.state });
                } else {
                    navigate(to);
                }
            },
            replace: (to, state) => {
                if (typeof to === 'string') {
                    navigate(to, { replace: true, state });
                } else if (to && typeof to === 'object') {
                    const path = to.pathname + (to.search || '');
                    navigate(path, { replace: true, state: to.state });
                } else {
                    navigate(to, { replace: true });
                }
            },
            go: (delta) => window.history.go(delta),
            goBack: () => navigate(-1),
            goForward: () => navigate(1),
            length: window.history.length,
            action: 'PUSH', // React Router v6 doesn't expose this, using default
            location: location,
            block: () => {
                console.warn('history.block() is not supported in React Router v6');
                return () => {};
            },
            listen: () => {
                console.warn('history.listen() is not supported in React Router v6');
                return () => {};
            },
            createHref: (to) => {
                if (typeof to === 'string') return to;
                if (to && typeof to === 'object') {
                    return to.pathname + (to.search || '');
                }
                return '';
            }
        }), [navigate, location]);
        
        // Create match object compatible with React Router v5 API
        const match = React.useMemo(() => ({
            params: params,
            path: location.pathname,
            url: location.pathname,
            isExact: true // React Router v6 doesn't have exact matching in the same way
        }), [params, location.pathname]);
        
        return (
            <Component
                {...props}
                // React Router v5 compatible API
                history={history}
                location={location}
                match={match}
                // React Router v6 API (for convenience)
                navigate={navigate}
                params={params}
            />
        );
    }
    
    // Set display name for better debugging
    WithRouterWrapper.displayName = `WithRouter(${Component.displayName || Component.name || 'Component'})`;
    
    return WithRouterWrapper;
}

export default WithRouter;

